import { Cartesian3, Cartesian2, Plane } from "cesium";
import Delaunator from "delaunator";
import earcut from "earcut";
import * as math from "mathjs";
import { EarthBrainUtils } from "./earthbrainutility";
export function getDelaunayTriangulation(pointsOfPolygon, normal) {
    const vertexBuffer = Cartesian3.packArray(pointsOfPolygon);
    const planeOrigin = pointsOfPolygon[0].clone();
    const plane = Plane.fromPointNormal(planeOrigin, normal);
    const projectedSecondPoint = pointsOfPolygon[1].clone();
    Plane.projectPointOntoPlane(plane, projectedSecondPoint, projectedSecondPoint);
    const xDir = EarthBrainUtils.getNormalizedDirection(planeOrigin, projectedSecondPoint);
    const yDir = Cartesian3.cross(normal, xDir, new Cartesian3());
    Cartesian3.normalize(yDir, yDir);
    // create a plane and project point on the plane and find the x, y point for triangulation
    const verticesWithNoZ = [];
    for (let i = 0; i < pointsOfPolygon.length; ++i) {
        const point = Cartesian3.clone(pointsOfPolygon[i], new Cartesian3());
        Cartesian3.subtract(point, planeOrigin, point);
        Plane.projectPointOntoPlane(plane, point, point);
        verticesWithNoZ.push(Cartesian3.dot(point, xDir), Cartesian3.dot(point, yDir));
    }
    const delaunay = new Delaunator(verticesWithNoZ);
    return {
        vertices: vertexBuffer,
        indices: [...delaunay.triangles],
    };
}
export function getNumberOfIntersectionsBetLineAndPolygon(line, polygon) {
    //get the start point and end point of the line
    const startPointLine = [line[0].x, line[0].y, line[0].z];
    const endPointLine = [line[1].x, line[1].y, line[1].z];
    let intersections = 0;
    //for each line segment of polygon find the intersection if they are intersecting
    for (let i = 0; i < polygon.length - 1; i++) {
        const startPointSeg = [polygon[i].x, polygon[i].y, polygon[i].z];
        const endPointSeg = [polygon[i + 1].x, polygon[i + 1].y, polygon[i + 1].z];
        const result = math.intersect(startPointLine, endPointLine, startPointSeg, endPointSeg);
        if (result.length != null) {
            intersections++;
        }
    }
    return intersections;
}
/**
 * Performs earcut triangulation on passed points
 * Returned Geometry is vertex buffer with triangulation
 * @param pointOfPolygon Points to be triangulated
 * @returns
 */
export function getEarcutTriangulation(pointOfPolygon) {
    const arrayForEarcut = Cartesian2.packArray(pointOfPolygon);
    const triangles = earcut(arrayForEarcut, undefined, 2);
    return { faces: triangles };
}
// Constants
export const EARTH_RADIUS = 6378137.0; // 赤道半径
// const GRS80_ECCENTRICITY = 0.081819191042815791; // GRS80 の離心率
const GRS80_ECCENTRICITY = 0.08181919104281579; // GRS80 の離心率
const ITERATION_LIMIT = 100;
const SMALL = 1.0e-10;
export function localToWgs84PointTransformation(point, siteInfo) {
    const east = point.x;
    const north = point.y;
    const local_z = point.z;
    // ローカル座標を回転する
    // Rotate local coordinates
    const r_e = siteInfo.falseEasting +
        ((east - siteInfo.falseEasting) * Math.cos(siteInfo.rotation) -
            (north - siteInfo.falseNorthing) * Math.sin(siteInfo.rotation));
    const r_n = siteInfo.falseNorthing +
        ((east - siteInfo.falseEasting) * Math.sin(siteInfo.rotation) +
            (north - siteInfo.falseNorthing) * Math.cos(siteInfo.rotation));
    // 緯度φにおける子午線面内の楕円の曲率半径
    // radius of curvature of ellipse in meridian plane at latitude φ
    const rho0 = (EARTH_RADIUS * (1.0 - Math.pow(GRS80_ECCENTRICITY, 2))) /
        Math.pow(1.0 -
            Math.pow(GRS80_ECCENTRICITY, 2) *
                Math.pow(Math.sin(siteInfo.originLatitude), 2), 3.0 / 2.0);
    // 緯度φにおける子午線に垂直な楕円の曲率半径
    // radius of curvature of ellipse perpendicular to meridian at latitude φ
    const nu0 = EARTH_RADIUS /
        Math.sqrt(1.0 -
            Math.pow(GRS80_ECCENTRICITY, 2) *
                Math.pow(Math.sin(siteInfo.originLatitude), 2));
    // 正接点（φo、λo）での投影の測地線源が与えられた共形球を定義するパラメータ
    // Parameters defining the conformal sphere given the geodesic source of the projection at the tangent point (φo, λo)
    const R = Math.sqrt(rho0 * nu0);
    const n = Math.sqrt(1.0 +
        (Math.pow(GRS80_ECCENTRICITY, 2) *
            Math.pow(Math.cos(siteInfo.originLatitude), 4)) /
            (1.0 - Math.pow(GRS80_ECCENTRICITY, 2)));
    const s1 = (1.0 + Math.sin(siteInfo.originLatitude)) /
        (1.0 - Math.sin(siteInfo.originLatitude));
    const s2 = (1.0 - GRS80_ECCENTRICITY * Math.sin(siteInfo.originLatitude)) /
        (1.0 + GRS80_ECCENTRICITY * Math.sin(siteInfo.originLatitude));
    const w1 = Math.pow(s1 * Math.pow(s2, GRS80_ECCENTRICITY), n);
    const sinchi0 = (w1 - 1.0) / (w1 + 1.0);
    const c = ((n + Math.sin(siteInfo.originLatitude)) * (1.0 - sinchi0)) /
        ((n - Math.sin(siteInfo.originLatitude)) * (1.0 + sinchi0));
    const w2 = c * w1;
    const chi0 = Math.asin((w2 - 1.0) / (w2 + 1.0));
    const l_lamda0 = siteInfo.originLongitude;
    const g = 2.0 * R * siteInfo.scaleFactor * Math.tan(Math.PI / 4.0 - chi0 / 2.0);
    const h = 4.0 * R * siteInfo.scaleFactor * Math.tan(chi0) + g;
    const i = Math.atan((r_e - siteInfo.falseEasting) / (h + r_n - siteInfo.falseNorthing));
    const j = Math.atan((r_e - siteInfo.falseEasting) / (g - r_n + siteInfo.falseNorthing)) - i;
    const chi = chi0 +
        2.0 *
            Math.atan((r_n -
                siteInfo.falseNorthing -
                (r_e - siteInfo.falseEasting) * Math.tan(j / 2.0)) /
                (2.0 * R * siteInfo.scaleFactor));
    const l_lamda = j + 2.0 * i + l_lamda0;
    const psi0 = (0.5 * Math.log((1.0 + Math.sin(chi)) / (c * (1.0 - Math.sin(chi))))) / n;
    const phi = new Array(ITERATION_LIMIT + 1);
    const psi = new Array(ITERATION_LIMIT + 1);
    phi[0] = 0;
    // 最初の近似
    // First approximation
    phi[1] = 2.0 * Math.atan(Math.pow(Math.E, psi0)) - Math.PI / 2.0;
    psi[1] = Math.log(Math.tan(phi[1] / 2.0 + Math.PI / 4.0) *
        Math.pow((1.0 - GRS80_ECCENTRICITY * Math.sin(phi[1])) /
            (1.0 + GRS80_ECCENTRICITY * Math.sin(phi[1])), GRS80_ECCENTRICITY / 2.0));
    // 緯度を計算する
    // Calculate latitude
    let k = 1;
    while (Math.abs(phi[k] - phi[k - 1]) > SMALL && k < ITERATION_LIMIT) {
        phi[k + 1] =
            phi[k] -
                ((psi[k] - psi0) *
                    Math.cos(phi[k]) *
                    Math.cos(1.0 - Math.pow(GRS80_ECCENTRICITY, 2) * Math.pow(Math.sin(phi[k]), 2))) /
                    (1.0 - Math.pow(GRS80_ECCENTRICITY, 2));
        psi[k + 1] = Math.log(Math.tan(phi[k] / 2.0 + Math.PI / 4.0) *
            Math.pow((1.0 - GRS80_ECCENTRICITY * Math.sin(phi[k])) /
                (1.0 + GRS80_ECCENTRICITY * Math.sin(phi[k])), GRS80_ECCENTRICITY / 2.0));
        k++;
    }
    // 経度を計算する
    // Calculate longitude
    const lamda = (l_lamda - l_lamda0) / n + l_lamda0;
    // 標高を計算する
    // Calculate the elevation
    const ellip = local_z -
        siteInfo.verticalOffset -
        (east - siteInfo.falseEasting) * siteInfo.inclineX -
        (north - siteInfo.falseNorthing) * siteInfo.inclineY;
    return Cartesian3.fromRadians(lamda, phi[k], ellip);
}
//# sourceMappingURL=extra-utils.js.map